import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Input } from "@/shared/ui/input";
import { Loader2, Package, Search } from "lucide-react";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { useServiceLineAvailableSubscriptionPlans, useUpdateServiceLineSubscriptionPlan } from "@/domains/service-lines/services/account-service-lines";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { Product } from '@/shared/types/productTypes';
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import EmptyView from "@/shared/components/EmptyView";
import { useMemo } from "react";
import SearchBar from "@/shared/components/SearchBar";
import { TerminalType } from "@/shared/types/userTerminalTypes";

interface SubscriptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  serviceLine: ServiceLine;
}

const SubscriptionsDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: SubscriptionsDialogProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  //TODO: Implement infinite scroll for products
  const { data: productsData, isLoading: isLoadingProducts } = useServiceLineAvailableSubscriptionPlans(serviceLine.serviceLineNumber, open);
  const updateSubscription = useUpdateServiceLineSubscriptionPlan();

  // Filter out products if the service line already has a product and if the service line has any terminals with L2VPN circuits, only show products that support L2VPN
  const availableProducts: Product[] = useMemo(() => {
    return productsData?.results?.filter(product =>
      product.orbiusProductReferenceId !== serviceLine.product?.orbiusProductReferenceId
    ) || [];
  }, [productsData?.results, serviceLine]);

  // Filter products based on search term
  const filteredProducts: Product[] = useMemo(() => {
    return availableProducts.filter(product =>
      product.orbiusProductName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableProducts, searchTerm]);

  useEffect(() => {
    if (!open) {
      setSelectedProduct(null);
      setSearchTerm("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) {
      showError(ERROR.SERVICE_LINE.UPDATE_SUBSCRIPTION_PLAN);
      return;
    }

    try {
      await updateSubscription.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        orbiusProductReferenceId: selectedProduct.orbiusProductReferenceId,
      });
      showSuccess(SUCCESS.SERVICE_LINE.UPDATED_SUBSCRIPTION_PLAN);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.UPDATE_SUBSCRIPTION_PLAN);
    }
  };

  const showSearch = availableProducts.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="update-subscription-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Update Subscription Plan</DialogTitle>
          <DialogDescription>{serviceLine?.serviceLineName}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              {/* Search Input - only show when there are products */}
              {showSearch && (
                <div className="relative">
                  <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search subscription plans..." />
                </div>
              )}

              <p className="text-sm font-medium">Select Subscription Plan</p>
              <ScrollArea className="rounded-md border border p-4 max-h-[300px]">
                {isLoadingProducts ? (
                  <div className="flex justify-center items-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : filteredProducts.length > 0 ? (
                  <RadioGroup
                    value={selectedProduct?.orbiusProductReferenceId}
                    onValueChange={(value) => {
                      const product = filteredProducts.find(p => p.orbiusProductReferenceId === value);
                      setSelectedProduct(product || null);
                    }}
                    className="space-y-2"
                  >
                    {filteredProducts.map((product) => (
                      <div key={product.orbiusProductReferenceId} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={product.orbiusProductReferenceId}
                          id={`product-${product.orbiusProductReferenceId}`}
                        />
                        <Label
                          htmlFor={`product-${product.orbiusProductReferenceId}`}
                          className="text-sm font-normal flex-1"
                        >
                          <div className="flex flex-col">
                            <span>{product.orbiusProductName}</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : searchTerm ? (
                  <EmptyView
                    icon={Search}
                    size="small"
                    title="No Subscription Plans Found"
                    description={`No subscription plans match "${searchTerm}". Try adjusting your search.`}
                  />
                ) : (
                  <EmptyView
                    icon={Package}
                    size="small"
                    title="No Subscription Plans Available"
                    description="There are no subscription plans available for this service line."
                  />
                )}
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={updateSubscription.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateSubscription.isPending || !selectedProduct}
              className="gap-2"
            >
              {updateSubscription.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Subscription Plan'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionsDialog; 